import { configureLocalization } from '@lit/localize';
import type { LocaleModule } from '@lit/localize';
import { sourceLocale, targetLocales } from './generated/locale-codes.js';
import type { Locale } from '../api/types.js';

/**
 * Runtime-mode localization so the locale can switch without a page reload
 * (language toggle in the branding bar, admin console).
 *
 * Workflow:
 *   1. wrap user-visible strings in msg()
 *   2. `npm run localize:extract`  → XLIFF for translators
 *   3. `npm run localize:build`    → regenerates src/i18n/locales/{nl,fr}.ts
 */
// Static per-locale import map: the bundler can only code-split dynamic imports it can analyse
// statically — a raw template-literal path leaks into the production bundle unresolved and 404s
// at runtime (no locale chunks are emitted), silently breaking the language switch.
const catalogs: Record<string, () => Promise<LocaleModule>> = {
  nl: () => import('./locales/nl.js'),
  fr: () => import('./locales/fr.js'),
};

export const { getLocale, setLocale } = configureLocalization({
  sourceLocale, // 'en'
  targetLocales, // ['fr', 'nl']
  loadLocale: (locale) => catalogs[locale](),
});

const SUPPORTED: readonly Locale[] = ['en', 'nl', 'fr'];

/** Type guard for a supported locale. */
export function isLocale(value: string): value is Locale {
  return (SUPPORTED as readonly string[]).includes(value);
}

/**
 * Pick the initial locale from the URL prefix the backend already uses (ARCHITECTURE §6.5).
 * Falls back to 'en'. The user's stored culture (from /api/session) takes over once known.
 */
export function initialLocaleFromUrl(path: string = location.pathname): Locale {
  const seg = path.split('/')[1] ?? '';
  return isLocale(seg) ? seg : 'en';
}

/** Apply a locale, guarding against unsupported values. Returns the resolved locale. */
export async function applyLocale(value: string): Promise<Locale> {
  const locale: Locale = isLocale(value) ? value : 'en';
  if (locale !== getLocale()) {
    await setLocale(locale);
  }
  document.documentElement.lang = locale;
  return locale;
}
